<template>
  <section class="carousel-section">
    <div class="carousel-container">
      <!-- Loading State -->
      <div v-if="isLoading" class="carousel-loading" role="status" aria-live="polite">
        <span class="sr-only">{{ $t('labels.loading', 'Loading...') }}</span>
      </div>

      <!-- Error State -->
      <div v-else-if="hasError" class="carousel-error">
        <p>{{ $t('errors.contentUnavailable', 'Content temporarily unavailable') }}</p>
      </div>

      <!-- Content -->
      <template v-else>
        <h2 v-if="title" class="carousel-title">{{ title }}</h2>
        <p v-if="description" class="carousel-description">{{ description }}</p>
        
        <div v-if="images && images.length" class="carousel-3d">
          <div class="carousel-viewport">
            <div class="carousel-track" :style="trackStyle">
              <div 
                v-for="(image, index) in images" 
                :key="index" 
                class="carousel-slide"
                :class="getSlideClass(index)"
                :style="getSlideStyle(index)"
                @click="goToSlide(index)"
              >
                <img :src="image.src" :alt="image.alt || ''" loading="lazy" />
                
                <!-- Navigation Controls - Only shown on active slide -->
                <div v-if="index === currentIndex" class="carousel-controls">
                  <button 
                    @click.stop="prevSlide" 
                    class="carousel-btn carousel-btn--prev"
                    aria-label="Previous slide"
                  >
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                      <polyline points="15 18 9 12 15 6"></polyline>
                    </svg>
                  </button>
                  
                  <div class="carousel-dots">
                    <button
                      v-for="(img, idx) in images"
                      :key="idx"
                      @click.stop="goToSlide(idx)"
                      class="carousel-dot"
                      :class="{ 'carousel-dot--active': idx === currentIndex }"
                      :aria-label="`Go to slide ${idx + 1}`"
                    ></button>
                  </div>
                  
                  <button 
                    @click.stop="nextSlide" 
                    class="carousel-btn carousel-btn--next"
                    aria-label="Next slide"
                  >
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                      <polyline points="9 18 15 12 9 6"></polyline>
                    </svg>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </template>
    </div>
  </section>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted } from 'vue'
import useCarousel from './Carousel'

const props = defineProps({
  content: { type: Object, default: null }
})

const {
  isLoading,
  hasError,
  title,
  description,
  images,
  fetchCarouselData,
  $t
} = useCarousel(props.content)

const currentIndex = ref(0)
const isTransitioning = ref(false)

const trackStyle = computed(() => ({
  transform: `translateX(0)` // We'll position slides individually
}))

const getSlideClass = (index) => {
  const diff = index - currentIndex.value
  const total = images.value.length
  
  // Normalize diff to handle wrapping
  let normalizedDiff = diff
  if (diff > total / 2) normalizedDiff = diff - total
  if (diff < -total / 2) normalizedDiff = diff + total
  
  if (normalizedDiff === 0) return 'carousel-slide--active'
  if (normalizedDiff === -1) return 'carousel-slide--prev'
  if (normalizedDiff === 1) return 'carousel-slide--next'
  return 'carousel-slide--hidden'
}

const getSlideStyle = (index) => {
  const diff = index - currentIndex.value
  const total = images.value.length
  
  // Normalize diff to handle wrapping
  let normalizedDiff = diff
  if (diff > total / 2) normalizedDiff = diff - total
  if (diff < -total / 2) normalizedDiff = diff + total
  
  // Calculate position and transformation for full-width layout
  const translateX = normalizedDiff * 60 // Increased to 60% spacing for wider spread
  const translateZ = Math.abs(normalizedDiff) === 1 ? -300 : 0 // Side images pushed further back
  const rotateY = normalizedDiff === -1 ? 25 : normalizedDiff === 1 ? -25 : 0 // Angle side images
  const scale = normalizedDiff === 0 ? 1 : 0.85 // Side images slightly smaller
  const opacity = Math.abs(normalizedDiff) > 1 ? 0 : 1
  
  return {
    transform: `translateX(${translateX}%) translateZ(${translateZ}px) rotateY(${rotateY}deg) scale(${scale})`,
    opacity,
    zIndex: normalizedDiff === 0 ? 30 : (Math.abs(normalizedDiff) === 1 ? 20 : 1)
  }
}

const nextSlide = () => {
  if (isTransitioning.value || images.value.length === 0) return
  isTransitioning.value = true
  currentIndex.value = (currentIndex.value + 1) % images.value.length
  setTimeout(() => { isTransitioning.value = false }, 600)
}

const prevSlide = () => {
  if (isTransitioning.value || images.value.length === 0) return
  isTransitioning.value = true
  currentIndex.value = (currentIndex.value - 1 + images.value.length) % images.value.length
  setTimeout(() => { isTransitioning.value = false }, 600)
}

const goToSlide = (index) => {
  if (isTransitioning.value || index === currentIndex.value) return
  isTransitioning.value = true
  currentIndex.value = index
  setTimeout(() => { isTransitioning.value = false }, 600)
}

// Auto-play functionality
let autoPlayInterval = null

const startAutoPlay = () => {
  stopAutoPlay()
  autoPlayInterval = setInterval(() => {
    nextSlide()
  }, 5000) // Change slide every 5 seconds
}

const stopAutoPlay = () => {
  if (autoPlayInterval) {
    clearInterval(autoPlayInterval)
    autoPlayInterval = null
  }
}

onMounted(() => {
  fetchCarouselData().then(() => {
    if (images.value.length > 0) {
      startAutoPlay()
    }
  })
})

onUnmounted(() => {
  stopAutoPlay()
})

// Expose methods for parent if needed
defineExpose({ fetchCarouselData, nextSlide, prevSlide, goToSlide })
</script>

<style lang="scss" scoped>
@use './Carousel.scss';
</style>